import { encodeAbiParameters, encodeFunctionData, getAddress, parseAbiParameters, } from "viem";
import { EAS_ABI } from "../abis.js";
import { getDeployment } from "../addresses.js";
import { encodeRegisterAgent } from "./vault.js";
import { unsupported } from "../errors.js";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const ZERO_BYTES32 = "0x0000000000000000000000000000000000000000000000000000000000000000";
const JOIN_REQUEST_PARAMS = parseAbiParameters("uint256, uint256, address, string");
const AGENT_APPROVED_PARAMS = parseAbiParameters("uint256, uint256, address");
const toBig = (x) => (typeof x === "bigint" ? x : BigInt(x));
// ── Message-prefix helpers (ported from cli/src/lib/eas.ts) ──
//
// Join-request messages carry CLI metadata as bracketed prefixes so a creator
// querying Base EAS can recover the source chain + referrer. Order is
// `[chain:<slug>] [ref:<id>] body` — chain outer, ref inner. Kept byte-identical
// to the CLI so calldata-built join requests are indistinguishable from
// CLI-signed ones in `sherwood fund requests`.
function formatMessageWithRef(message, referrerAgentId) {
    if (referrerAgentId == null)
        return message;
    return `[ref:${referrerAgentId}] ${message}`;
}
function formatMessageWithChain(message, sourceSlug, easSlug) {
    if (sourceSlug === easSlug)
        return message;
    return `[chain:${sourceSlug}] ${message}`;
}
/** Build an EAS `attest` tx (revocable, no expiry, no refUID, zero value). */
function buildAttestTx(easAddress, schema, recipient, data, chainId) {
    return {
        to: getAddress(easAddress),
        data: encodeFunctionData({
            abi: EAS_ABI,
            functionName: "attest",
            args: [
                {
                    schema,
                    data: {
                        recipient,
                        expirationTime: 0n,
                        revocable: true,
                        refUID: ZERO_BYTES32,
                        data,
                        value: 0n,
                    },
                },
            ],
        }),
        value: "0x0",
        chainId,
    };
}
/**
 * Encode a SYNDICATE_JOIN_REQUEST EAS attestation (agent → creator).
 *
 * The attest tx ALWAYS lands on Base (where coordination attestations live),
 * even when the fund runs on another chain — a `[chain:<slug>]` prefix is
 * added to the message so the creator can recover the source chain. Throws
 * UNSUPPORTED on chains with no EAS path (Robinhood).
 */
export function encodeJoinRequest(args, chainId) {
    const dep = getDeployment(chainId);
    const eas = dep.eas;
    if (eas.address === ZERO_ADDRESS) {
        throw unsupported(`No EAS coordination path on chain ${chainId} — join requests require a chain whose attestations are pinned to Base.`);
    }
    const easSlug = getDeployment(eas.chainId).slug;
    const message = formatMessageWithChain(formatMessageWithRef(args.message, args.referrerAgentId), dep.slug, easSlug);
    const data = encodeAbiParameters(JOIN_REQUEST_PARAMS, [
        toBig(args.fundId),
        toBig(args.agentId),
        getAddress(args.vault),
        message,
    ]);
    return {
        txs: [
            buildAttestTx(eas.address, eas.schemas.joinRequest, getAddress(args.creator), data, eas.chainId),
        ],
        preconditions: [],
        description: `Request to join fund ${toBig(args.fundId).toString()} (vault ${getAddress(args.vault)}).`,
        note: "EAS coordination attestation — always submitted on Base, even for funds on another chain. Revocable; the creator discovers it via the join-request schema.",
    };
}
/**
 * Encode the two txs a creator submits to approve an agent:
 *   1. `vault.registerAgent(agentAddress, agentId)` on the fund's own chain
 *   2. AGENT_APPROVED EAS attestation on Base
 *
 * These land on two different chains — `txs[0].chainId` is the fund chain,
 * `txs[1].chainId` is Base.
 */
export function encodeApproveAgent(args, chainId) {
    const dep = getDeployment(chainId);
    const eas = dep.eas;
    if (eas.address === ZERO_ADDRESS) {
        throw unsupported(`No EAS coordination path on chain ${chainId} — approvals require a chain whose attestations are pinned to Base.`);
    }
    const registerTx = encodeRegisterAgent({
        vault: args.vault,
        agentAddress: args.agentAddress,
        agentId: toBig(args.agentId),
    }, chainId).txs[0];
    const data = encodeAbiParameters(AGENT_APPROVED_PARAMS, [
        toBig(args.fundId),
        toBig(args.agentId),
        getAddress(args.vault),
    ]);
    const attestTx = buildAttestTx(eas.address, eas.schemas.agentApproved, getAddress(args.agentAddress), data, eas.chainId);
    return {
        txs: [registerTx, attestTx],
        preconditions: [],
        description: `Approve agent ${toBig(args.agentId).toString()} for fund ${toBig(args.fundId).toString()}: register on the vault (chain ${chainId}) + AGENT_APPROVED attestation on Base.`,
        note: "Vault owner only. Two txs on two chains: registerAgent runs on the fund's chain; the approval attestation runs on Base.",
    };
}
